"use client";

import { useState, useEffect } from "react";
import { useSupabaseAuth } from "./use-supabase-auth";
import { getSupabase } from "@/lib/supabase";

export interface Conquista {
  id: string;
  usuario_id: string;
  usuario_nome: string;
  usuario_email: string;
  tarefa_id: string | null;
  titulo: string;
  descricao: string | null;
  tipo: 'tarefa' | 'epico' | 'iniciativa';
  criado_em: string;
  atualizado_em: string;
}

export function useConquistasSupabase() {
  const { user } = useSupabaseAuth();
  const [conquistas, setConquistas] = useState<Conquista[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buscarConquistas = async (amigosIds?: string[]) => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      console.log('Buscando conquistas. User ID:', user.id, 'Amigos IDs:', amigosIds);

      let query = getSupabase()
        .from('conquistas')
        .select('*')
        .order('criado_em', { ascending: false });

      // Se fornecidos IDs de amigos, buscar apenas conquistas do usuário e dos amigos
      if (amigosIds && amigosIds.length > 0) {
        query = query.or(`usuario_id.eq.${user.id},usuario_id.in.(${amigosIds.join(',')})`);
        console.log('Query com amigos:', `usuario_id.eq.${user.id},usuario_id.in.(${amigosIds.join(',')})`);
      } else {
        // Se não, buscar apenas conquistas do usuário
        query = query.eq('usuario_id', user.id);
        console.log('Query sem amigos:', `usuario_id.eq.${user.id}`);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Buscar nomes dos usuários da tabela pública
      const userIds = [...new Set((data || []).map(c => c.usuario_id))];
      const { data: perfis, error: perfisError } = await getSupabase()
        .from('usuarios_publicos')
        .select('user_id, nome, email')
        .in('user_id', userIds);

      if (perfisError) {
        console.error('Erro ao buscar perfis:', perfisError);
      }

      const perfilMap = new Map((perfis || []).map(p => [p.user_id, p]));

      // Mapear os dados para incluir nome e email do usuário
      const conquistasComUsuario = (data || []).map(conquista => {
        const perfil = perfilMap.get(conquista.usuario_id);
        return {
          ...conquista,
          usuario_nome: perfil?.nome || '',
          usuario_email: perfil?.email || '',
        };
      });

      console.log('Conquistas encontradas:', conquistasComUsuario);
      setConquistas(conquistasComUsuario);
    } catch (err) {
      setError('Erro ao buscar conquistas');
      console.error('Erro ao buscar conquistas:', err);
    } finally {
      setLoading(false);
    }
  };

  const criarConquista = async (dados: {
    titulo: string;
    descricao?: string;
    tipo: 'tarefa' | 'epico' | 'iniciativa';
    tarefa_id?: string;
  }) => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error } = await getSupabase()
        .from('conquistas')
        .insert({
          usuario_id: user.id,
          titulo: dados.titulo,
          descricao: dados.descricao || null,
          tipo: dados.tipo,
          tarefa_id: dados.tarefa_id || null
        })
        .select()
        .single();

      if (error) throw error;

      setConquistas(prev => [data, ...prev]);
      return data;
    } catch (err) {
      setError('Erro ao criar conquista');
      console.error('Erro ao criar conquista:', err);
    } finally {
      setLoading(false);
    }
  };

  const removerConquista = async (conquistaId: string) => {
    try {
      setLoading(true);
      setError(null);

      const { error } = await getSupabase()
        .from('conquistas')
        .delete()
        .eq('id', conquistaId);

      if (error) throw error;

      setConquistas(prev => prev.filter(c => c.id !== conquistaId));
    } catch (err) {
      setError('Erro ao remover conquista');
      console.error('Erro ao remover conquista:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      buscarConquistas();
    }
  }, [user]);

  return {
    conquistas,
    loading,
    error,
    criarConquista,
    removerConquista,
    buscarConquistas,
    recarregar: () => buscarConquistas()
  };
}
